'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { IconMobile } from '@/app/components/icons/hardware/icon-mobile';
import { IconLine } from '@/app/components/icons/social-media/icon-line';
import { Image } from '@/app/components/media/image';
import { Link } from '@/app/components/navigation/link';
import { useOverlay } from '@/app/components/overlays/hooks/useOverlay';
import { getDealerGoogleMapUrl, isDealerCV, toLineLink } from '@/app/components/sections/dealer/utils/mapping-dealer';
import { DealerDetail } from '@/app/components/sections/special-offer/detail/search-dealer-contact/dealer-info-card/dealer-detail';
import type { DealerInfoCardProps } from '@/app/components/sections/special-offer/detail/search-dealer-contact/types';
import { SPECIAL_OFFER_DATA_TEST, SPECIAL_OFFER_EVENT_NAME } from '@/app/components/sections/special-offer/tracking';
import { MODAL_KEYS } from '@/app/constants/overlayKeys';
import { MY_ISUZU_APPOINTMENT } from '@/app/lcv/contents/service/service-shared.data';
import { isMobileDevice } from '@/app/utils/browser';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
const DownloadMyIsuzuModal = dynamic(() => import('@/app/components/sections/special-offer/detail/search-dealer-contact/dealer-info-card/my-isuzu-modal').then(mod => mod.DownloadMyIsuzuModal), {
  ssr: false
});
export const DealerInfoCard = t0 => {
  const $ = _c(48);
  const {
    dealer
  } = t0;
  const lineId = (isDealerCV ? dealer.afterSaleLineCv : dealer.afterSaleLineLcv) ?? null;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isMobileDevice();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isDesktop = !t1;
  const hasMap = !!(dealer.lat || dealer.lng);
  const mapUrl = hasMap ? getDealerGoogleMapUrl({
    google_map_url: dealer.googleMapUrl,
    ...dealer
  }, dealer.name_th) : "";
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      overlayKey: MODAL_KEYS.MODAL_CUSTOM,
      instanceId: "my-isuzu-dealer-download"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const modal = useOverlay(t2);
  let t3;
  if ($[2] !== modal) {
    t3 = () => {
      if (isDesktop) {
        return modal.open();
      }
    };
    $[2] = modal;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleMyIsuzuClick = t3;
  const t4 = "rounded-sm bg-white p-4";
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = createDataAttributes({
      test: "dealer-info-result"
    });
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = "space-y-1";
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createDataAttributes({
      test: "result-header"
    });
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = createDataAttributes({
      test: "name"
    });
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = dealer.name_th ?? "-";
  let t10;
  if ($[7] !== t9) {
    t10 = <h4 className="text-body-2 font-noto-bold col-span-2" {...t8}>{t9}</h4>;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = "flex justify-between gap-x-4";
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = createDataAttributes({
      test: "result-address"
    });
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = createDataAttributes({
      test: "address"
    });
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const t14 = dealer.afterSaleAddress ?? "-";
  let t15;
  if ($[11] !== t14) {
    t15 = <p className="text-body-2 text-gray-700" {...t13}>{t14}</p>;
    $[11] = t14;
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  const t16 = !!hasMap && <Link href={mapUrl} target="_blank" aria-label="Google Maps" className="ease-bounce self-start transition-transform duration-330 hover:-translate-y-0.5" {...createDataAttributes({
    test: "button-google-maps"
  })} {...createDataGA({
    click: true,
    eventName: SPECIAL_OFFER_EVENT_NAME,
    group: SPECIAL_OFFER_DATA_TEST.DEALER_MAP,
    url: mapUrl,
    text: "Google Maps",
    value: dealer.name_th ?? ""
  })}><Image className="size-14" src="https://assets.isuzu-tis.com/google_maps_668114ddfa.png" alt="Google Maps" width={56} height={56} aspectRatio="aspect-1-1" sizes="56px" /></Link>;
  let t17;
  if ($[13] !== t12 || $[14] !== t15 || $[15] !== t16) {
    t17 = <div className={t11} {...t12}>{t15}{t16}</div>;
    $[13] = t12;
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== t10 || $[18] !== t17 || $[19] !== t7) {
    t18 = <div className={t6} {...t7}>{t10}{t17}</div>;
    $[17] = t10;
    $[18] = t17;
    $[19] = t7;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== dealer || $[22] !== lineId) {
    t19 = <DealerDetail dealer={dealer} lineId={lineId} />;
    $[21] = dealer;
    $[22] = lineId;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t18 || $[25] !== t19 || $[26] !== t5) {
    t20 = <div className={t4} {...t5}>{t18}{t19}</div>;
    $[24] = t18;
    $[25] = t19;
    $[26] = t5;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = createDataAttributes({
      test: "dealer-search-buttons"
    });
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  const t22 = !lineId;
  let t23;
  if ($[29] !== t22) {
    t23 = cn({
      "mx-auto w-auto": t22
    });
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconMobile />;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = createDataAttributes({
      test: "button-0"
    });
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = createDataGA({
      click: true,
      eventName: SPECIAL_OFFER_EVENT_NAME,
      group: SPECIAL_OFFER_DATA_TEST.BOOKING_MYI,
      url: isDesktop ? undefined : MY_ISUZU_APPOINTMENT,
      text: "\u0E19\u0E31\u0E14\u0E2B\u0E21\u0E32\u0E22\u0E1C\u0E48\u0E32\u0E19 my-ISUZU"
    });
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== handleMyIsuzuClick || $[35] !== t23) {
    t27 = <Button className={t23} size="sm" color="red" width="full" iconBefore={t24} target="_blank" href={isDesktop ? undefined : MY_ISUZU_APPOINTMENT} onClick={handleMyIsuzuClick} {...t25} {...t26}>นัดหมายผ่าน my-ISUZU</Button>;
    $[34] = handleMyIsuzuClick;
    $[35] = t23;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== lineId) {
    t28 = !!lineId && <Button variant="secondary" size="sm" color="red" width="full" iconBefore={<IconLine />} href={toLineLink(lineId)} target="_blank" {...createDataAttributes({
      test: "button-1"
    })} {...createDataGA({
      eventName: SPECIAL_OFFER_EVENT_NAME,
      group: SPECIAL_OFFER_DATA_TEST.BOOKING_LINE,
      click: true,
      url: toLineLink(lineId),
      text: "\u0E19\u0E31\u0E14\u0E2B\u0E21\u0E32\u0E22\u0E1C\u0E48\u0E32\u0E19 LINE"
    })}>นัดหมายผ่าน LINE</Button>;
    $[37] = lineId;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== t27 || $[40] !== t28) {
    t29 = <ButtonGroup className="mt-4 md:gap-4 xl:flex-nowrap xl:gap-6" {...t21}>{t27}{t28}</ButtonGroup>;
    $[39] = t27;
    $[40] = t28;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== modal) {
    t30 = !!isDesktop && <DownloadMyIsuzuModal {...modal.overlayProps} onClose={() => modal.close()} />;
    $[42] = modal;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== t20 || $[45] !== t29 || $[46] !== t30) {
    t31 = <>{t20}{t29}{t30}</>;
    $[44] = t20;
    $[45] = t29;
    $[46] = t30;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  return t31;
};